// Utility functions for Monthly Report components

/**
 * Format number with thousand separators
 */
export const formatNumber = (value) => {
  if (typeof value === "string") {
    const num = parseFloat(value);
    return isNaN(num)
      ? value
      : num.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }
  return value?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00";
};

/**
 * Format percentage with 4 decimal places
 */
export const formatPercentage = (value) => {
  if (typeof value === "string") {
    const num = parseFloat(value);
    return isNaN(num)
      ? value
      : num.toLocaleString("en-US", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        });
  }
  return value?.toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) || "0.0000";
};

/**
 * Determine if an amount is positive
 */
export const isPositiveAmount = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

/**
 * Format date YYYYMMDD to readable format
 */
export const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const dateStr = dateValue.toString();
  if (dateStr.length === 8) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${month}/${day}/${year}`;
  }
  return dateValue;
};

/**
 * Generate CSV data from monthly report data
 * Returns array of rows for CSV export
 */
export const generateMonthlyReportCSV = (reportData) => {
  if (!reportData) return null;

  const { userType } = reportData;
  const rows = [];
  let totalNetResidual = 0;

  // Helper function to format amount with $
  const formatAmount = (value) => {
    const num = parseFloat(value || 0);
    return `$${num.toFixed(2)}`;
  };

  // Helper function to add merchant row based on user type
  const addMerchantRow = (merchant, isoName = "", acquirerName = "") => {
    const volume = parseFloat(merchant.summary?.total_transaction_amount || 0);
    const mdrRevenue = parseFloat(merchant.summary?.total_percent_tx_processing || 0);
    const midFee = parseFloat(merchant.summary?.total_monthly_amount_peer_mid || 0);
    const procFee = parseFloat(merchant.summary?.total_fiat_tx_processing || 0);
    const netResidual = parseFloat(merchant.summary?.total_fees || 0);

    totalNetResidual += netResidual;

    // Base row with merchant info and amounts
    const baseRow = [
      merchant.merchantName || "",
      merchant.merchantID || "",
      formatAmount(volume),
      formatAmount(mdrRevenue),
      formatAmount(midFee),
      formatAmount(procFee),
      formatAmount(netResidual),
    ];

    // Add columns based on user type
    if (userType === "admin") {
      // Admin: show both ISO Name and Acquirer Name
      baseRow.push(isoName, acquirerName);
    } else if (userType === "acquirer") {
      // Acquirer: show only ISO Name
      baseRow.push(isoName);
    }
    // ISO and Merchant: don't add ISO/Acquirer columns

    rows.push(baseRow);
  };

  // Process based on user type
  switch (userType) {
    case "admin":
      // Admin: iterate through acquirers -> direct_merchants and isos -> merchants
      if (reportData.acquirers && Array.isArray(reportData.acquirers)) {
        reportData.acquirers.forEach((acquirer) => {
          const acquirerName = acquirer.acquirerName || acquirer.acquirerID || "";

          // Direct merchants
          if (acquirer.direct_merchants && Array.isArray(acquirer.direct_merchants)) {
            acquirer.direct_merchants.forEach((merchant) => {
              addMerchantRow(merchant, "", acquirerName);
            });
          }

          // ISOs and their merchants
          if (acquirer.isos && Array.isArray(acquirer.isos)) {
            acquirer.isos.forEach((iso) => {
              const isoName = iso.isoName || iso.isoID || "";
              if (iso.merchants && Array.isArray(iso.merchants)) {
                iso.merchants.forEach((merchant) => {
                  addMerchantRow(merchant, isoName, acquirerName);
                });
              }
            });
          }
        });
      }
      break;

    case "acquirer":
      // Acquirer: direct_merchants and isos -> merchants
      if (reportData.direct_merchants && Array.isArray(reportData.direct_merchants)) {
        reportData.direct_merchants.forEach((merchant) => {
          addMerchantRow(merchant);
        });
      }

      if (reportData.isos && Array.isArray(reportData.isos)) {
        reportData.isos.forEach((iso) => {
          const isoName = iso.isoName || iso.isoID || "";
          if (iso.merchants && Array.isArray(iso.merchants)) {
            iso.merchants.forEach((merchant) => {
              addMerchantRow(merchant, isoName);
            });
          }
        });
      }
      break;

    case "iso":
      // ISO: only merchants
      if (reportData.merchants && Array.isArray(reportData.merchants)) {
        const isoName = reportData.isoName || reportData.isoID || "";
        reportData.merchants.forEach((merchant) => {
          addMerchantRow(merchant, isoName);
        });
      }
      break;

    case "merchant":
      // Merchant: single merchant (if available in reportData)
      if (reportData.merchantID && reportData.merchantName) {
        const merchant = {
          merchantID: reportData.merchantID,
          merchantName: reportData.merchantName,
          summary: reportData.summary,
        };
        const isoName = reportData.isoName || reportData.isoID || "";
        addMerchantRow(merchant, isoName);
      }
      break;

    default:
      break;
  }

  // Add total row
  const totalRow = [
    "Total",
    "",
    "",
    "",
    "",
    "",
    formatAmount(totalNetResidual),
  ];

  // Add empty columns for ISO/Acquirer based on user type
  if (userType === "admin") {
    totalRow.push("", "");
  } else if (userType === "acquirer") {
    totalRow.push("");
  }

  rows.push(totalRow);

  return { rows, userType };
};

/**
 * Download CSV file from monthly report data
 */
export const downloadMonthlyReportCSV = (reportData) => {
  const result = generateMonthlyReportCSV(reportData);
  if (!result || !result.rows || result.rows.length === 0) {
    return;
  }

  const { rows, userType } = result;

  // Base headers
  const baseHeaders = [
    "Merchant Name",
    "Merchant ID",
    "Amount",
    "MDR Revenue",
    "MID Fee",
    "Proc Fee",
    "Net Residual",
  ];

  // Add headers based on user type
  let headers = [...baseHeaders];
  if (userType === "admin") {
    headers.push("ISO Name", "Acquirer Name");
  } else if (userType === "acquirer") {
    headers.push("ISO Name");
  }
  // ISO and Merchant: no additional headers

  // Build CSV content: headers, data rows (excluding total row), and total row
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(","))
    .join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  // Generate filename with date
  const dateStr = reportData.date ? reportData.date.toString() : "report";
  const filename = `residuals_report_${dateStr}.csv`;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};


